import { useT } from '@/i18n/react';
import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Avatar } from '@/components/ActivityCard';
import { CellHexIcon, signedCells } from '@/components/RivalScore';
import { useBackHandler } from '@/hooks/useBackHandler';
import { useSwipeToClose } from '@/hooks/useSwipeToClose';
import { SheetGrip } from '@/components/ui';
import { formatNumber } from '@/lib/format';
import type { ActivityRivalOther, Rival } from '@/lib/api';
import './rivalExplainSheet.css';

/**
 * A rivális-sáv magyarázata — alulról feljövő lap (GRUNDO #62).
 *
 * Geri kérése: a sáv nem mindenkinek egyértelmű, ezért koppintásra nem a
 * rivális profilja nyílik, hanem ez a lap. Minden sor UGYANAZT A PIRULÁT
 * mutatja, ugyanazzal a színnel és formával, mint a sáv — így a néző a lapon
 * a sávra ismer, és látja, melyik szám mit jelent.
 *
 * Sorrend (Geri): ebben a körben elvett · a riválistól összesen elvett ·
 * a rivális által összesen elvett · rivális szorzó · a kör további károsultjai.
 * A profilon és a Riválisok fülön nincs „kör", ott az első és az utolsó
 * rész kimarad.
 *
 * ⚠️ PORTÁLBAN, a `<body>` alatt: a feedben a kártyák egy transzformált
 * elemben ülnek (lásd `PhotoLightbox`), ahol a `position: fixed` nem a
 * képernyőhöz igazodna.
 *
 * ⚠️ A GYÖKÉR ELNYELI A KATTINTÁST. A portál a DOM-ban kívül van, de a React
 * a kattintást a KOMPONENSFÁN buborékoltatja tovább — egy szülő `onClick`
 * (pl. a sáv gombja) a bezárás után azonnal újranyitná a lapot.
 */
export function RivalExplainSheet({
  rival,
  selfName,
  takenThisActivity,
  others = [],
  onClose,
}: {
  rival: Rival;
  /** A bal oldal gazdája — „Te" a saját nézetben, egyébként a szerző neve. */
  selfName: string;
  /** Csak aktivitás-kártyán: a körben a fő riválistól elvett cellák. */
  takenThisActivity?: number;
  /** Csak aktivitás-kártyán: a kör további károsultjai. */
  others?: readonly ActivityRivalOther[];
  onClose: () => void;
}) {
  const t = useT();
  const navigate = useNavigate();
  const encounters = rival.gainedEvents + rival.lostEvents;

  /* Android vissza gomb: a lap záródjon, ne a képernyő váltson. */
  useBackHandler(true, () => onClose());

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Lefelé húzva is lecsukható (#71).
  const panelRef = useSwipeToClose<HTMLDivElement>(onClose);

  function openProfile(username: string) {
    onClose();
    navigate(`/felhasznalo/${encodeURIComponent(username)}`);
  }

  // A legtöbbet vesztett elöl (Geri). A szerver már így küldi, de a sorrend a
  // lap ígérete, ezért itt is rögzítjük.
  const sortedOthers = [...others].sort(
    (a, b) => (b.cellsThisActivity ?? 0) - (a.cellsThisActivity ?? 0),
  );

  return createPortal(
    <div
      className="rexp"
      role="dialog"
      aria-modal="true"
      aria-labelledby="rexp-title"
      onClick={(event) => event.stopPropagation()}
    >
      <button type="button" className="rexp__scrim" aria-label={t.rivalExplain.close} onClick={onClose} />

      <div ref={panelRef} className="rexp__panel">
        <SheetGrip />
        <h2 id="rexp-title" className="rexp__title">
          {t.rivalExplain.title}
        </h2>

        <button type="button" className="rexp__person" onClick={() => openProfile(rival.username)}>
          <Avatar url={rival.photoURL} name={rival.username} size={36} />
          <span className="rexp__vs">
            {selfName} {t.rivalExplain.and} <strong>{rival.username}</strong>
          </span>
          <ChevronIcon />
        </button>

        <ul className="rexp__legend">
          {takenThisActivity !== undefined ? (
            <LegendRow
              pill={<Pill tone="dark">{signedCells('+', takenThisActivity)}<CellHexIcon /></Pill>}
              label={t.rivalExplain.activity}
              hint={
                selfName === t.feed.card.you
                  ? t.rivalExplain.youTook(rival.username)
                  : t.rivalExplain.theyTook(selfName, rival.username)
              }
            />
          ) : null}
          <LegendRow
            pill={<Pill tone="gain">{signedCells('+', rival.gainedCells)}<CellHexIcon /></Pill>}
            label={t.rivalExplain.gained}
            hint={t.rivalExplain.gainedHint}
          />
          <LegendRow
            pill={<Pill tone="loss">{signedCells('−', rival.lostCells)}<CellHexIcon /></Pill>}
            label={t.rivalExplain.lost}
            hint={t.rivalExplain.lostHint}
          />
          <LegendRow
            pill={<Pill tone="dark">{formatNumber(encounters)}×</Pill>}
            label={t.rivalExplain.multiplier}
            hint={t.rivalExplain.multiplierHint}
          />
        </ul>

        {sortedOthers.length > 0 ? (
          <section className="rexp__others">
            <h3 className="rexp__subtitle">{t.rivalExplain.others}</h3>
            {sortedOthers.map((other) => (
              <button
                key={other.uid}
                type="button"
                className="rexp__other"
                onClick={() => openProfile(other.username)}
              >
                <Avatar url={other.photoURL} name={other.username} size={28} />
                <span className="rexp__other-name">{other.username}</span>
                {other.cellsThisActivity !== undefined ? (
                  <span className="rexp__other-count">
                    {signedCells('+', other.cellsThisActivity)}
                    <CellHexIcon />
                  </span>
                ) : null}
                <ChevronIcon />
              </button>
            ))}
          </section>
        ) : null}

        <button type="button" className="rexp__close" onClick={onClose}>
          {t.rivalExplain.close}
        </button>
      </div>
    </div>,
    document.body,
  );
}

function LegendRow({ pill, label, hint }: { pill: ReactNode; label: string; hint: string }) {
  return (
    <li className="rexp__row">
      <span className="rexp__pill-slot">{pill}</span>
      <span className="rexp__text">
        <span className="rexp__label">{label}</span>
        <span className="rexp__hint">{hint}</span>
      </span>
    </li>
  );
}

/** A sáv pirulájának mása — a `tone` ugyanazt a színt adja, mint a sávon. */
function Pill({ tone, children }: { tone: 'dark' | 'gain' | 'loss'; children: ReactNode }) {
  return <span className={`rexp__pill rexp__pill--${tone}`}>{children}</span>;
}

function ChevronIcon() {
  return (
    <svg
      className="rexp__chevron"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M9 5l7 7-7 7" />
    </svg>
  );
}
